import React, { Fragment, useEffect, useRef, useState } from 'react';

import { GMetrixComboBoxList } from 'components/GMetrixComboBoxList/GMetrixComboBoxList';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { License, LicenseReturnable, TransactionApi } from 'apis/TransactionApi';
import { chunkArray, getMostRestrictedLicenseDeliverable } from 'utils/objects';
import { DEFAULT_TRANSACTION_FORM } from 'components/NewAccessCodeForm';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { Instructor, InstructorApi } from 'apis/InstructorApi';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { FullfilledApiResponse } from 'apis/ApiCaller';
import { Col, Container, Form } from 'react-bootstrap';
import { ToolTip } from 'components/ToolTip/ToolTip';
import { useNotification } from 'app/notifications';
import { TransactionType } from 'types';
import { useApp } from 'app';

import 'components/NewGroupForm/newGroupForm.scss';

interface NewGroupProps {
    ActionsWrapper?: React.ElementType;
    ContentWrapper?: React.ElementType;
    newClassroomData: any;
    onClose?: () => void;
    onSuccess?: (classroomId: number) => void;
}

const NewGroupForm = ({ ActionsWrapper = Fragment, ContentWrapper = Fragment, newClassroomData, onClose, onSuccess }: NewGroupProps) => {

    const [groupName, setGroupName] = useState('');
    const [isInvalidProps, setIsInvalideProps] = useState({ name: { message: '', validType: '' }, identifier: { message: '', validType: '' }, instructors: { message: '', validType: '' } });
    const [codeIdentifier, setCodeIdentifier] = useState('');
    const [groupDescription, setGroupDescription] = useState('');
    const [instructors, setInstructors] = useState<Instructor[]>([]);
    const [selectedInstructors, setSelectedInstructors] = useState<Instructor[]>([]);
    const { userId } = useApp();
    const [maxStudents, setMaxStudents] = useState(0);
    const [maxUses, setMaxUses] = useState(0);
    const [isLimited, setLimited] = useState(false);
    const [licenses, setLicenses] = useState<License[]>([]);
    const [enableBulb, setEnableBulb] = useState(false);
    const [enableCloud, setEnableCloud] = useState(false);
    const { formatMessage } = useIntl();
    const gmetrixConfirmRef = useRef<any>(null);
    const { catchWithToast, createToast } = useNotification();
    const [loadingSaveButton, setLoadingSaveButton] = useState(false);

    useEffect(() => {

        handleGMetrixTableData();

        // set licenses
        TransactionApi.GetAllLicenses().then(result => handleAvailableUsesMaxValue(result)).catch((error: unknown) => catchWithToast(error, formatMessage({ id: 'failed_to_load_license_data.ccdc93b9', defaultMessage: 'Failed to load license data.' })));

    }, []);

    const handleAvailableUsesMaxValue = (licenses: LicenseReturnable) => {

        const restrictedLicense = getMostRestrictedLicenseDeliverable(licenses.licenses ?? [], newClassroomData.content);
        const maxUses = restrictedLicense.avaliableStudentsAllowed === -1 ? 999 : restrictedLicense.avaliableStudentsAllowed;

        setMaxUses(maxUses);
        const selectedOrderIds = new Set((newClassroomData.content ?? []).map((c: any) => c.RestrictedOrderId));
        setLicenses((licenses.licenses ?? []).filter((l: License) => selectedOrderIds.has(l.OrderId)));

    };

    const handleGMetrixTableData = async () => {

        const instructorListInTable: Instructor[] = await InstructorApi.GetAll();
        instructorListInTable.map(instructor => {

            if (instructor.Id === userId) {

                setSelectedInstructors([...selectedInstructors, instructor]);

            }

        });
        setInstructors(instructorListInTable);

    };

    // eslint-disable-next-line no-async-promise-executor
    const createGroup = async () => {

        setLoadingSaveButton(true);
        const invalidProps = { name: { message: '', validType: '' }, identifier: { message: '', validType: '' }, instructors: { message: '', validType: '' } };
        if (selectedInstructors.length === 0) {

            invalidProps.instructors = { message: formatMessage({ id: 'this_classroom_must_have_at_least_one_in.d3ac119d', defaultMessage: 'This Classroom must have at least one instructor.' }), validType: 'invalid' };
            setLoadingSaveButton(false);

        }
        if (groupName.length === 0) {

            invalidProps.name = { message: formatMessage({ id: 'minimum_of_1_character_is_required.d92aa14a', defaultMessage: 'Minimum of 1 character is required.' }), validType: 'invalid' };
            setLoadingSaveButton(false);

        }
        if (invalidProps.name.validType !== '' || invalidProps.identifier.validType !== '' || invalidProps.instructors.validType !== '') {

            setLoadingSaveButton(false);
            setIsInvalideProps(invalidProps);

            return;

        }
        let success: FullfilledApiResponse | undefined;
        try {

            success = await StudentGroupApi.CreateEditStudentGroup(
                0,
                groupName,
                groupDescription,
                maxStudents,
                true,
                codeIdentifier ? codeIdentifier : undefined
            );

        } catch (e) {

            catchWithToast(e, formatMessage({ id: 'an_error_occured_with_creating_the_class.72a6f1af', defaultMessage: 'An error occured with creating the Classroom' }) || 'Failed to create classroom.');

        }

        if (success) {

            if (success.Done) {

                const details = newClassroomData.transactionDetails ?? DEFAULT_TRANSACTION_FORM;
                let result: FullfilledApiResponse | undefined;
                try {

                    result = await TransactionApi.Create({
                        ...details, HasBulbAccess: enableBulb, HasCloud: enableCloud,
                        Identifier: codeIdentifier,
                        IndividualMaxStudents: maxStudents,
                        Name: groupName,
                        TransactionType: TransactionType.Classroom
                    });

                } catch (e) {

                    catchWithToast(e, formatMessage({ id: 'an_error_occured_with_creating_the_class.72a6f1af', defaultMessage: 'An error occured with creating the Classroom' }) || 'Failed to create classroom.');

                }

                if (result && result.Id && success.ExtraData) {

                    try {

                        await TransactionApi.AddGroupToTransaction([result.Id], [success.ExtraData]);

                    } catch (e) {

                        catchWithToast(e, formatMessage({ id: 'an_error_occured_with_creating_the_class.72a6f1af', defaultMessage: 'An error occured with creating the Classroom' }) || 'Failed to create classroom.');

                    }

                    let chunkedTestArray: any[] = [];
                    let chunkedBookArray: any[] = [];
                    let chunkedCourseArray: any[] = [];
                    let chunkUpload = false;

                    if (newClassroomData.transactionDetails.PendingTests.length > 100 ||
                                newClassroomData.transactionDetails.PendingBooks.length > 100 ||
                                newClassroomData.transactionDetails.PendingCourses.length > 100
                    ) {

                        chunkedTestArray = chunkArray(newClassroomData.transactionDetails.PendingTests, 20);
                        chunkedBookArray = chunkArray(newClassroomData.transactionDetails.PendingBooks, 20);
                        chunkedCourseArray = chunkArray(newClassroomData.transactionDetails.PendingCourses, 20);
                        chunkUpload = true;

                    }

                    if (chunkUpload) {

                        let maxAmount = chunkedTestArray.length;
                        if (chunkedBookArray.length > maxAmount) {

                            maxAmount = chunkedBookArray.length;

                        }
                        if (chunkedCourseArray.length > maxAmount) {

                            maxAmount = chunkedCourseArray.length;

                        }
                        for (let index = 0; index < maxAmount; index++) {

                            try {

                                await TransactionApi.AddContentToTransaction(
                                    result.Id,
                                    chunkedTestArray[index] ?? [],
                                    chunkedBookArray[index] ?? [],
                                    chunkedCourseArray[index] ?? []
                                );

                            } catch (e) {

                                catchWithToast(e, formatMessage({ id: 'failed_to_add_content_to_classroom.4d7f49d5', defaultMessage: 'Failed to add content to classroom.' }));

                            }

                        }

                    } else {

                        try {

                            await TransactionApi.AddContentToTransaction(
                                result.Id,
                                newClassroomData.transactionDetails.PendingTests ?? [],
                                newClassroomData.transactionDetails.PendingBooks ?? [],
                                newClassroomData.transactionDetails.PendingCourses ?? []
                            );

                        } catch (e) {

                            catchWithToast(e, formatMessage({ id: 'failed_to_add_content_to_classroom.4d7f49d5', defaultMessage: 'Failed to add content to classroom.' }));

                        }

                    }

                }

                const instructorIds = selectedInstructors.map(instructor => instructor.Id);
                if (instructorIds.length > 0 && success.ExtraData) {

                    try {

                        await StudentGroupApi.AddEditInstructorByGroup(
                            [success.ExtraData],
                            [...instructorIds],
                            false
                        );

                    } catch (e) {

                        catchWithToast(e, formatMessage({ id: 'unable_to_add_instructors.aef62b89', defaultMessage: 'Unable to add instructors' }) || 'Failed to add instructors.');

                    }

                }
                createToast(formatMessage({ id: 'classroom_created_successfully.215646d5', defaultMessage: 'Classroom Created Successfully' }), 'success');

                // The classroom exists once CreateEditStudentGroup succeeds, so open it even if a follow-up step failed.
                if (success.ExtraData) onSuccess?.(success.ExtraData);

            } else {

                createToast(formatMessage({ id: 'an_error_occured_with_creating_the_class.72a6f1af', defaultMessage: 'An error occured with creating the Classroom' }), 'danger');

            }

        }
        setLoadingSaveButton(false);

    };

    return (
        <>
            <ContentWrapper>
                <GMetrixDialogConfirm ref={gmetrixConfirmRef} />

                <Container fluid className="details">
                    <GMetrixInput
                        autoFocus
                        required
                        onChange={e => {

                            setGroupName(e.target.value);

                        }}
                        className="mb-2"
                        feedbackMessage={isInvalidProps.name.message}
                        label={formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' })}
                        maxLength={50}
                        validType={isInvalidProps.name.validType as any}
                        value={groupName}
                    />

                    <Form.Group as={Col} className="my-2" controlId="formIdentifier">
                        <GMetrixInput
                            onChange={e => {

                                setCodeIdentifier(e.target.value.replace(/[^a-zA-Z0-9]/g, ''));

                            }}
                            feedbackMessage={isInvalidProps.identifier.message}
                            label={formatMessage({ id: 'identifier_optional.6ca7cdc8', defaultMessage: 'Identifier (optional)' })}
                            maxLength={8}
                            validType={isInvalidProps.identifier.validType as any}
                            value={codeIdentifier}
                        />
                        <Form.Text className="text-muted">
                            <FormattedMessage defaultMessage="Used for organizational purposes, EX. MOS2024" id="used_for_organizational_purposes_ex_mos2.d1b7c962" />
                        </Form.Text>
                    </Form.Group>

                    <Form.Group>

                        <Form.Label className="m-0 fw-bold">
                            <FormattedMessage defaultMessage="Number of Students Allowed" id="number_of_students_allowed.41cbfb2c" />
                        </Form.Label>
                        <small className="text-muted ms-2 mb-2">
                            <FormattedMessage defaultMessage="(license total now" id="license_total_now.7e373c06" />
                            {maxUses}
                            <FormattedMessage defaultMessage=", will change as uses are redeemed)" id="will_change_as_uses_are_redeemed.e8f5ac0f" />
                        </small>

                        <ToolTip
                            content={(
                                <p>
                                    <FormattedMessage defaultMessage="This setting controls the maximum number of students who can join this Classroom. This license currently has" id="this_setting_controls_the_maximum_number.ba8f1dfb" />
                                    {maxUses}
                                    {' '}
                                    <FormattedMessage defaultMessage="seats in total." id="seats_in_total.bf0ce960" />
                                    {' '}
                                </p>
                            )} className="mb-1"
                        />
                        <div>

                            <GMetrixCheckInput
                                onChange={e => {

                                    setMaxStudents(0);
                                    setLimited(false);

                                }}
                                checked={!isLimited}
                                infoTitle={formatMessage({ id: 'this_classroom_can_use_as_many_seats_as.1de71b49', defaultMessage: 'This Classroom can use as many seats as are available in the license pool. Availability may change as other Classrooms sharing the license enroll students.' })}
                                label={formatMessage({ id: 'no_limit.059f752f', defaultMessage: 'No limit' })}
                                subLabel="(up to license total)"
                                type={'radio'}
                            />

                            {/* this margin cancels out the input margin for keeping it in line */}
                            <div className="d-flex justify-content start" style={{ marginTop: '15px' }}>

                                <GMetrixCheckInput
                                    label={(
                                        <div className="d-flex text-nowrap" >
                                            <FormattedMessage defaultMessage="Limit to" id="limit_to.afeb0f97" />

                                            <GMetrixInput
                                                onChange={e => {

                                                    const maxValue: number = parseInt(e.target.value);
                                                    if (maxValue > maxUses) {

                                                        setMaxStudents(maxUses);

                                                    } else if (maxValue === 0) {

                                                        setMaxStudents(1);

                                                    } else {

                                                        setMaxStudents(maxValue);

                                                    }

                                                }}
                                                className="mx-2"
                                                disabled={!isLimited}
                                                max={maxUses}
                                                min={1}
                                                style={{ marginTop: '-15px', width: 100 }}
                                                type="number"
                                                value={maxStudents}
                                            />
                                            students
                                            <ToolTip
                                                content={(
                                                    <p>
                                                        <FormattedMessage defaultMessage="Enrollments for this Classroom will stop at the number you set, even if more seats are available on the license." id="enrollments_for_this_classroom_will_stop.7f5cf92c" />
                                                    </p>
                                                )} className="mb-1"
                                            />
                                        </div>
                                    )}
                                    onChange={e => {

                                        setMaxStudents(1);
                                        setLimited(true);

                                    }}
                                    checked={isLimited}
                                    type={'radio'}
                                />
                            </div>
                        </div>
                    </Form.Group>

                    {(licenses.some(license => license.BulbAccessUses > 0) || licenses.some(license => license.CloudAccessUses > 0)) && (
                        <Form.Group className="mt-3 mb-3">
                            <Form.Label className="m-0 fw-bold">
                                <FormattedMessage defaultMessage="Additional Features" id="additional_features.22ca02d5" />
                            </Form.Label>
                            <div className="mt-2">
                                {licenses.some(license => license.BulbAccessUses > 0) && (
                                    <GMetrixCheckInput
                                        checked={enableBulb}
                                        disabled={loadingSaveButton}
                                        infoTitle={formatMessage({ id: 'enables_bulb_access.67a4b911', defaultMessage: 'Enables bulb access' })}
                                        label={formatMessage({ id: 'enable_bulb_portfolio.a0c8d39b', defaultMessage: 'Enable bulb Portfolio' })}
                                        onChange={e => setEnableBulb(e.target.checked)}
                                        type="checkbox"
                                    />
                                )}
                                {licenses.some(license => license.CloudAccessUses > 0) && (
                                    <GMetrixCheckInput
                                        checked={enableCloud}
                                        disabled={loadingSaveButton}
                                        infoTitle={formatMessage({ id: 'enables_cloud_access.1d170166', defaultMessage: 'Enables cloud access' })}
                                        label={formatMessage({ id: 'cloud_access.95fe7c27', defaultMessage: 'Cloud Access' })}
                                        onChange={e => setEnableCloud(e.target.checked)}
                                        type="checkbox"
                                    />
                                )}
                            </div>
                        </Form.Group>
                    )}

                    <GMetrixComboBoxList
                        className="mb-2"
                        dataList={instructors}
                        displayNameProp="FirstName"
                        displayNameSecondaryProp="LastName"
                        feedbackMessage={isInvalidProps.instructors.message}
                        infoTitle={formatMessage({ id: 'instructors_are_users_who_can_manage_cla.1dd0e940', defaultMessage: 'Instructors are users who can manage Classrooms, assign content, edit settings, and manually add students.' })}
                        label={formatMessage({ id: 'instructors.92fd6e0a', defaultMessage: 'Instructors*' })}
                        selectedListItems={selectedInstructors}
                        setDataList={setSelectedInstructors}
                        validType={isInvalidProps.instructors.validType as any}
                    />
                    <GMetrixInput
                        onChange={e => {

                            setGroupDescription(e.target.value);

                        }}
                        as="textarea"
                        className="mb-2"
                        label={formatMessage({ id: 'description.e1c24171', defaultMessage: 'Description', description: 'Formulario' })}
                        rows={4}
                        type="text"
                        value={groupDescription}
                    />
                </Container>
            </ContentWrapper>
            <ActionsWrapper>
                {onClose && (
                    <GMetrixButton className="mx-2" onClick={onClose} variant="secondary">
                        <FormattedMessage defaultMessage="Back" id="back.b6238c9e" />
                    </GMetrixButton>
                )}
                <GMetrixButton disabled={loadingSaveButton} onClick={createGroup} variant="primary" >
                    {loadingSaveButton ? <i className="fa-solid fa-spinner-third fa-spin" ></i> : <FormattedMessage defaultMessage="Create" id="create.be56b47d" />}
                </GMetrixButton>
            </ActionsWrapper>
        </>
    );

};

export default NewGroupForm;